import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Identity, WebPubSubRequest, WebPubSubService } from '@festo-ax/smartenance-shared-ui';
import { catchError, concatMap, from, map, Observable, of, switchMap } from 'rxjs';
import * as profileActions from 'app/actions/profile';
import * as sapSynchronizationActions from 'app/actions/sap-synchronization';
import { SapConnectionStatus } from 'app/models/reducers/sap-synchronization';
import * as fromProfile from 'app/reducers/profile';
import * as fromSapSynchronization from 'app/reducers/sap-synchronization';
import { DataPrivacyService } from 'app/services/data-privacy.service';
import { FeatureFlagService } from 'app/services/feature-flag.service';
import { SapSynchronizationUtils } from 'app/utils/sap-synchronization.utils';
import { SapUtils } from 'app/utils/reducers/sap.utils';
import { FALLBACK_LANGUAGE } from 'app/tokens';
import { UserSettings } from 'app/models/api/user-settings';
import { LicenseFeatureFlag } from 'app/models/license';
import { concatLatestFrom } from '@ngrx/operators';

@Injectable()
export class SapSynchronizationEffects {
  private actions$: Actions = inject(Actions);
  private dataPrivacyService: DataPrivacyService = inject(DataPrivacyService);
  private webPubSubService: WebPubSubService = inject(WebPubSubService);
  private store: Store = inject(Store);
  private featureFlagService: FeatureFlagService = inject(FeatureFlagService);
  private readonly sapLanguageFallback: string = inject(FALLBACK_LANGUAGE);

  acceptSapConnectorUsageAgreement$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(sapSynchronizationActions.AcceptSapConnectorUsageAgreement),
      switchMap(() =>
        this.dataPrivacyService.acceptSapConnectorUsageAgreement().pipe(
          concatMap(result => [
            profileActions.reloadIdentity(),
            profileActions.updateSapConnectorAgreementState({ sapConnectorAgreementAcceptedState: result.accepted }),
            sapSynchronizationActions.AcceptSapConnectorUsageAgreementSuccess()
          ]),
          catchError(error => of(sapSynchronizationActions.AcceptSapConnectorUsageAgreementFail(error)))
        )
      )
    );
  });

  checkSapConnection$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapSynchronizationActions.TestSapConnection),
      map(() => <WebPubSubRequest<'checkConnectionStatus'>>{ topic: 'checkConnectionStatus' }),
      concatLatestFrom(() => [this.store.select(fromProfile.selectProfile), this.store.select(fromProfile.selectUserSettings)]),
      switchMap(([request, identity, userSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity
          )
        ).pipe(
          map(value => {
            return value
              ? sapSynchronizationActions.TestSapConnectionSuccess({ sapConnectionStatus: SapConnectionStatus.ONLINE })
              : sapSynchronizationActions.TestSapConnectionSuccess({ sapConnectionStatus: SapConnectionStatus.OFFLINE });
          }),
          catchError(error => of(sapSynchronizationActions.TestSapConnectionFail({ error })))
        )
      )
    )
  );

  loadSapEquipment$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapSynchronizationActions.loadSapEquipment),
      concatLatestFrom(() => [
        this.store.select(fromProfile.getSapConnectorAgreementAccepted),
        this.store.select(fromSapSynchronization.getSapEquipment)
      ]),
      switchMap(([action, sapConnectorAgreementAccepted, sapEquipment]) =>
        from(this.featureFlagService.featureOn(LicenseFeatureFlag.SAP)).pipe(
          map(hasSapConnectorLicense =>
            SapSynchronizationUtils.ShouldLoadSapEquipment(
              action.sapEquipmentId,
              action.isSapMachine,
              sapConnectorAgreementAccepted,
              hasSapConnectorLicense,
              sapEquipment
            )
              ? sapSynchronizationActions.getSapEquipment({ sapEquipmentId: action.sapEquipmentId })
              : sapSynchronizationActions.skipSapEquipmentLoading()
          )
        )
      )
    )
  );

  getSapEquipment$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapSynchronizationActions.getSapEquipment),
      concatLatestFrom(() => this.store.select(fromProfile.getSapLanguage)),
      map(
        ([action, language]) =>
          ({
            topic: 'getEquipmentDetails',
            data: { id: action.sapEquipmentId, language }
          } as WebPubSubRequest<'getEquipmentDetails'>)
      ),
      concatLatestFrom(() => [
        this.store.select(fromProfile.selectProfile),
        this.store.select(fromProfile.selectUserSettings)
      ]),
      switchMap(([request, identity, userSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity
          )
        ).pipe(
          map(value => {
            if (value?.data?.item != null) {
              return sapSynchronizationActions.getSapEquipmentSuccess({ sapEquipment: value.data.item })
            }

            const error: string =  value.data?.topicHandlingError?.Message || 'getSapEquipment response is null';
            return sapSynchronizationActions.getSapEquipmentFail({ error });
            }
          ),
          catchError(error => of(sapSynchronizationActions.getSapEquipmentFail({ error })))
        )
      )
    )
  );

  sendTestRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapSynchronizationActions.sendTestRequest),
      map(action => ({
        topic: action.topic,
        data: action.request
      })),
      concatLatestFrom(() => [this.store.select(fromProfile.selectProfile), this.store.select(fromProfile.selectUserSettings)]),
      // here according implementation could be provided any topic, so using any here is justified
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      switchMap(([request, identity, userSettings]: [WebPubSubRequest<any>, Identity, UserSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity
          )
        ).pipe(
          map(value =>
            value?.data != null
              ? sapSynchronizationActions.sendTestRequestSuccess({ responseData: value?.data })
              : sapSynchronizationActions.sendTestRequestFail({ error: 'Not able to send test request' })
          ),
          catchError(error => of(sapSynchronizationActions.sendTestRequestFail({ error })))
        )
      )
    )
  );
}
